import {
  type OfferPerfCtaPoint,
  type OfferPerformanceCtaReport,
  type OfferPerfV2Point,
  type ReportFilter,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';

export type OfferPerformanceReportRow = OfferPerfV2Point | OfferPerfCtaPoint;

export const toLanguageCustomerFilter = (
  language: string | null | undefined
): { key: 'language'; value: string | null } => ({
  key: 'language',
  value: language || null,
});

export const buildCustomerReportFiltersFromPivotGroups = <
  TRow,
  TKey extends string,
>(
  groupObject: { key: TKey }[] | undefined,
  selectedItem: TRow | undefined,
  reverseLookup: Partial<
    Record<TKey, (row: TRow) => { key: string; value: unknown } | undefined>
  >
): ReportFilter[][] => {
  if (!selectedItem || !groupObject) return [];

  return groupObject
    .map((group) => {
      const field = reverseLookup[group.key]?.(selectedItem);
      if (!field) return undefined;
      return [
        {
          field: field.key,
          operator: 'equals',
          value: field.value,
        } satisfies ReportFilter,
      ];
    })
    .filter(isDefined);
};

export const buildOfferPerformanceCtaRows = (
  offerCtaData: OfferPerfCtaPoint[],
  offerData: OfferPerfV2Point[]
): OfferPerfCtaPoint[] => [
  ...offerCtaData.map((row) => ({
    ...row,
    totalViews: 0,
    ctaTotalViews: row.totalViews,
  })),
  ...offerData.map(
    (row) =>
      ({
        funnelAudContentCta: `${row.funnelAudContent},l_0`,
        language: row.language,
        totalViews: row.totalViews,
      }) as OfferPerfCtaPoint
  ),
];

export const getOfferPerformanceCtaRows = (
  report: Pick<OfferPerformanceCtaReport, 'offers'> | null | undefined
): OfferPerfCtaPoint[] => {
  if (!report?.offers) {
    return [];
  }

  const [offerData, offerCtaData] = report.offers;
  if (!Array.isArray(offerCtaData)) {
    return [];
  }

  return buildOfferPerformanceCtaRows(offerCtaData, offerData);
};

/** Matches play-offer-perf / offer-performance-v2 pivot export data selection. */
export const getOfferPerformanceReportRows = (
  report: Pick<OfferPerformanceCtaReport, 'offers'> | null | undefined
): OfferPerformanceReportRow[] => {
  if (!report?.offers) {
    return [];
  }

  const offerData = report.offers[0];
  const ctaRows = getOfferPerformanceCtaRows(report);

  return ctaRows.length > 0 ? ctaRows : offerData;
};
